import _ from 'lodash';
import React from 'react';
import { WppTypography } from 'buildingBlocks';
import { Flight, EnvironmentVariables } from 'utils/types';
import { optionsUpdated } from 'containers/StrategyAnalytics/constants/actionsConstants';
import METRICS from 'containers/StrategyAnalytics/constants/metricsConstants';
import { getMetricOptions } from 'containers/StrategyAnalytics//utils/metricsUtils';
import { shownAsValue, dateRangeValue } from 'containers/StrategyAnalytics/constants/strategyAnalyticsConstants';
import { OptionValue, SegmentGroup, DropdownItems } from 'containers/StrategyAnalytics/types';
import { menuStyle } from 'containers/StrategyAnalytics/styles';
import 'containers/StrategyAnalytics/style.scss';
import DropdownOption from './DropdownOption';
import SegmentFilter from './SegmentFilter';
import ObjectFilter from './ObjectFilter';

type BySegment = {
  value: string
  text: string
};

type StrategyAnalyticsOptionsProps = {
  optionValue: OptionValue
  onChange: Function
  segments: DropdownItems
  segmentGroups: Array<SegmentGroup>
  showSegments: boolean
  flights: { [key: string]: Flight } | Array<Flight>
  onDateRangeChange: Function
  env: EnvironmentVariables
  metricOptions?: typeof METRICS
  bySegment?: BySegment
};

const StrategyAnalyticsOptions: React.FC<StrategyAnalyticsOptionsProps> = (props: StrategyAnalyticsOptionsProps): React.ReactElement => {
  const { optionValue, onChange, segments, segmentGroups, showSegments, flights, onDateRangeChange, env, metricOptions } = props;

  const aggregatorOptions = _.isEqual(env, EnvironmentVariables.demo)
    ? _.filter(getMetricOptions('aggregator', metricOptions), (o: { value: string, text: string }) => !_.isEqual(o.value, 'cost'))
    : getMetricOptions('aggregator', metricOptions);

  const filterFn = _.isEqual(env, EnvironmentVariables.demo)
    ? (o: { value: string, text: string }) => !_.isEqual(o.value, 'currentPacing') && !o.text.includes('Net') && !_.isEqual(o.value, 'margin')
    : (o: { value: string, text: string }) => !_.isEqual(o.value, 'currentPacing');

  const ratePercentageOptions = _.filter(getMetricOptions('ratePercentage', metricOptions), filterFn);

  return (
    <div id="analytics-options" style={{ ...menuStyle.container, gap: '8px' }}>
      <DropdownOption
        id="option-metric"
        defaultValue={optionValue.aggregator}
        optionsValue={aggregatorOptions}
        onChange={(selected) => onChange(optionsUpdated.aggregator, selected)}
        style={{ width: 220 }}
      />
      <WppTypography type="s-body" tag="span">vs</WppTypography>
      <DropdownOption
        id="option-rate"
        defaultValue={optionValue.ratePercentage}
        optionsValue={ratePercentageOptions}
        onChange={(selected) => onChange(optionsUpdated.ratePercentage, selected)}
        style={{ width: 180 }}
      />
      <WppTypography type="s-body" tag="span">shown</WppTypography>
      <DropdownOption
        id="option-aggregate"
        defaultValue={optionValue.shownAs}
        optionsValue={shownAsValue}
        onChange={(selected) => onChange(optionsUpdated.shownAs, selected)}
        style={{ width: 180 }}
      />
      <WppTypography type="s-body" tag="span">by</WppTypography>
      <ObjectFilter
        id="option-by"
        flights={flights}
        defaultValue={optionValue.byObject}
        onClick={onChange}
      />
      {showSegments && (
        <>
          <WppTypography type="s-body" tag="span">and</WppTypography>
          <SegmentFilter
            id="option-segments"
            segments={segments}
            segmentGroups={segmentGroups}
            defaultValue={optionValue.bySegment}
            onClick={onChange}
          />
        </>
      )}
      <WppTypography type="s-body" tag="span">for</WppTypography>
      <DropdownOption
        id="option-range"
        defaultValue={optionValue.dateRange}
        optionsValue={dateRangeValue}
        onChange={(selected) => onDateRangeChange(selected)}
        style={{ width: 200 }}
      />
    </div>
  );
};

export default StrategyAnalyticsOptions;
